import React from "react";
import PropTypes from "prop-types";

import DomainSelect from "@/checkout/view_cart/DomainSelect";
import EmailPlanCard from "./EmailPlanCard";
import PoweredByTitan from "@/common/images/logos/powered_by_titan.svg";
import { tokenList } from "@/common/util";

import "./email_plan_step.scss";

/**
 * Titan plan selection, scoped to one cart domain at a time. Adding a plan
 * commits it straight to the cart, so nothing here is held back as draft state
 * beyond each card's mailbox count.
 *
 * @param {string} activeDomain
 * @param {array<string>} domains
 */
function EmailPlanStep({
  activeDomain,
  domains,
  plans,
  titanServiceTypes,
  serverError,
  hideTitle,
  highlightPremium,
  showAllFeatures,
  header,
  actions
}) {
  const cssClasses = tokenList({
    "email-plan-step": true,
    "hide-title": hideTitle,
    "highlight-titan": highlightPremium
  });

  return (
    <div className={cssClasses}>
      {actions.skipEmailStep && (
        <div className="skip-container">
          <button
            type="button"
            className={"btn primary"}
            onClick={actions.skipEmailStep}
          >
            Skip
          </button>
        </div>
      )}

      {header || defaultHeader(domains, activeDomain, titanServiceTypes, actions.selectEmailDomain)}

      {serverError && <div className="error_msg">{serverError}</div>}

      <div className="plans">
        {plans.map((plan) => (
          <EmailPlanCard
            key={plan.serviceType}
            plan={plan}
            showAllFeatures={showAllFeatures}
            onAdd={({ planId, quantity }) =>
              actions.addEmailPlan({ domain: activeDomain, planId, quantity })
            }
          />
        ))}
      </div>

      <p className="powered-by">
        <img src={PoweredByTitan} alt="Powered by Titan" className="powered-by-logo" />
      </p>
    </div>
  );
}

EmailPlanStep.propTypes = {
  actions: PropTypes.object.isRequired,
  activeDomain: PropTypes.string.isRequired,
  domains: PropTypes.arrayOf(PropTypes.string).isRequired,
  plans: PropTypes.array.isRequired,
  titanServiceTypes: PropTypes.object.isRequired,
  serverError: PropTypes.string,
  header: PropTypes.object,
  highlightPremium: PropTypes.bool,
  showAllFeatures: PropTypes.bool
};

function defaultHeader(domains, activeDomain, titanServiceTypes, onSelect) {
  const title = (
    <h1 className="title">Choose a plan for {activeDomain}</h1>
  );

  if (domains.length <= 1) {
    return (
      <header>
        {title}
        <p className="subtitle">
          Choose a Titan Email plan. Billed annually per mailbox.
        </p>
      </header>
    );
  }

  return (
    <header>
      {title}
      <p className="subtitle">
        Pick a Titan Email plan for the selected domain. Billed annually per mailbox.
      </p>

      <DomainSelect
        activeDomain={activeDomain}
        domains={domains}
        titanServiceTypes={titanServiceTypes}
        onSelect={onSelect}
      />
    </header>
  );
}

export default EmailPlanStep;
